/*
 * InstallPrompt — cross-platform "install this app" flow.
 *
 * Handles 3 cases:
 *   1. Android / desktop Chrome / Edge — uses the native `beforeinstallprompt`
 *      event. We intercept it, show our own nicer banner, and defer the native
 *      prompt until the user taps "Install".
 *   2. iOS Safari — has no programmatic install API. Show illustrated
 *      instructions: "tap Share → Add to Home Screen".
 *   3. Already running as a standalone PWA (display-mode: standalone OR
 *      window.navigator.standalone on iOS) — render nothing.
 *
 * Dismissal is remembered in localStorage for 14 days per device so we don't
 * nag field techs every time they open the app.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Share, Plus, Smartphone, Download, Check } from "lucide-react";
import { isStandalone, detectPlatform, isDismissed, markDismissed } from "@/lib/pwa";

const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_COOLDOWN_DAYS = 14;

export default function InstallPrompt({ user }) {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState("unknown");
  const [nativePromptEvt, setNativePromptEvt] = useState(null);
  const [installedJustNow, setInstalledJustNow] = useState(false);

  // Only techs & admins should see this prompt — customers shouldn't be nagged
  // to install a "work app". Fail-closed: if user is null/unauth, don't show.
  const isStaff = !!(user && (user.is_technician || user.is_admin));

  useEffect(() => {
    if (!isStaff) return;
    if (isStandalone()) return;
    if (isDismissed(DISMISS_KEY, DISMISS_COOLDOWN_DAYS)) return;
    const p = detectPlatform();
    setPlatform(p);

    // Android / Chrome / Edge: capture the beforeinstallprompt event
    const onBIP = (e) => {
      e.preventDefault();
      setNativePromptEvt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    const onAppInstalled = () => {
      setInstalledJustNow(true);
      setNativePromptEvt(null);
      // Keep banner visible for 2s to show the success state
      setTimeout(() => setVisible(false), 2000);
    };
    window.addEventListener("appinstalled", onAppInstalled);

    // iOS Safari: no native event. Show our custom banner 8s after first visit
    // so it's not in the user's face on landing.
    let iosTimer;
    if (p === "ios-safari") {
      iosTimer = setTimeout(() => setVisible(true), 8000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onAppInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [isStaff]);

  // If the user logs out (or role changes to non-staff) while the prompt is up,
  // hide it immediately — no lingering "install" CTA for a customer view.
  useEffect(() => {
    if (!isStaff) setVisible(false);
  }, [isStaff]);

  const handleClose = () => {
    markDismissed(DISMISS_KEY);
    setVisible(false);
  };

  const handleNativeInstall = async () => {
    if (!nativePromptEvt) return;
    nativePromptEvt.prompt();
    try {
      const { outcome } = await nativePromptEvt.userChoice;
      if (outcome === "accepted") {
        setInstalledJustNow(true);
        setTimeout(() => setVisible(false), 2000);
      } else {
        markDismissed(DISMISS_KEY);
        setVisible(false);
      }
    } catch {
      setVisible(false);
    }
  };

  if (!isStaff || !visible) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] max-w-md w-[calc(100%-2rem)] rounded-2xl border bg-white shadow-2xl p-4 animate-in slide-in-from-bottom-8 fade-in"
      data-testid="install-prompt"
    >
      <button
        onClick={handleClose}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
        aria-label="Dismiss"
        data-testid="install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      {installedJustNow ? (
        <div className="flex items-center gap-3 py-1" data-testid="install-success">
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
            <Check className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-semibold">Installed!</div>
            <div className="text-xs text-gray-500">Launch OC Tasker from your home screen.</div>
          </div>
        </div>
      ) : platform === "ios-safari" ? (
        <IosInstructions onClose={handleClose} />
      ) : (
        <NativeInstallPromo onInstall={handleNativeInstall} onClose={handleClose} platform={platform} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function NativeInstallPromo({ onInstall, onClose, platform }) {
  return (
    <div className="pr-6">
      <div className="flex items-start gap-3">
        <img loading="lazy" decoding="async" src="/icons/icon-192.png" alt="OC Tasker" className="w-12 h-12 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900">Install OC Tasker</div>
          <div className="text-sm text-gray-600">
            Add to your home screen for one-tap access to clocking in, jobs, and Ask Chat.
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button variant="outline" size="sm" onClick={onClose} className="flex-1" data-testid="install-not-now">
          Not now
        </Button>
        <Button onClick={onInstall} size="sm" className="flex-1 bg-tenant-primary hover:bg-tenant-primary" data-testid="install-accept">
          <Download className="w-4 h-4 mr-1" />
          {platform === "android" ? "Install" : "Install app"}
        </Button>
      </div>
    </div>
  );
}

function IosInstructions({ onClose }) {
  return (
    <div className="pr-6">
      <div className="flex items-start gap-3">
        <img loading="lazy" decoding="async" src="/icons/icon-192.png" alt="OC Tasker" className="w-12 h-12 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 flex items-center gap-1.5">
            <Smartphone className="w-4 h-4 text-tenant-primary" />
            Add OC Tasker to your home screen
          </div>
          <div className="text-sm text-gray-600">
            One-tap access to clock-in, jobs, and Ask Chat — no App Store.
          </div>
        </div>
      </div>
      <ol className="text-sm text-gray-700 mt-3 space-y-1.5 pl-1" data-testid="install-ios-steps">
        <li className="flex items-start gap-2">
          <span className="mt-0.5 w-5 h-5 rounded-full bg-tenant-primary text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
          <span>Tap the <Share className="w-3.5 h-3.5 inline -mt-0.5 text-blue-500" aria-label="Share" /> <strong>Share</strong> button at the bottom of Safari</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="mt-0.5 w-5 h-5 rounded-full bg-tenant-primary text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
          <span>Scroll down and tap <strong>"Add to Home Screen"</strong> <Plus className="w-3.5 h-3.5 inline -mt-0.5" aria-label="Plus" /></span>
        </li>
        <li className="flex items-start gap-2">
          <span className="mt-0.5 w-5 h-5 rounded-full bg-tenant-primary text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
          <span>Tap <strong>"Add"</strong> in the top-right corner. Done!</span>
        </li>
      </ol>
      <div className="flex gap-2 mt-3">
        <Button variant="outline" size="sm" onClick={onClose} className="w-full" data-testid="install-ios-got-it">
          Got it
        </Button>
      </div>
    </div>
  );
}
